import { useEffect, useRef, useState } from 'preact/hooks';
import { logError } from '@/sdkConsole';
import { t, useT } from '@/i18n/t';

// PromoApplyResult is the parent's verdict on an entered/removed code. The parent
// owns validation — the Stripe Checkout Session on the paid path, the SM validate
// endpoint on the trial path — and reports success or a field-level message here.
// On success the parent flips appliedCode (and discountLabel) via props.
export interface PromoApplyResult {
  ok: boolean;
  // Shown on the field when ok is false.
  message?: string;
}

// AppliedPromoChip is the "code applied" pill: check icon, the code, and its
// discount descriptor. PromoCodeInput renders its remove button beside it;
// read-only surfaces (the success screen) render it alone.
export function AppliedPromoChip({ code, label }: { code: string; label?: string }) {
  return (
    <>
      {/* The filled check-circle, shared with the plan picker's code-applied
          band — an applied code reads as the same element on every step. */}
      <svg class="sm-promo-applied-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.15 14.35-4.1-4.1 1.4-1.4 2.7 2.7 5.7-5.7 1.4 1.4z" />
      </svg>
      {/* title carries the full code: the chip truncates a long one with an
          ellipsis (styles.css) rather than break the token mid-word. */}
      <span class="sm-promo-applied-code" title={code}>{code}</span>
      {/* A sibling flex item, not inline in the code span: the sidebar wraps
          it to its own line under the code (see styles.css), which an inline
          span can't do without breaking mid-phrase. The "·" separator is the
          ::before in styles.css. */}
      {label && <span class="sm-promo-applied-descriptor">{label}</span>}
    </>
  );
}

interface Props {
  // Applies the entered code. On success the parent updates appliedCode; on
  // failure it returns a message the field shows.
  onApply: (code: string) => Promise<PromoApplyResult>;
  // Removes the applied code (recompute the session on the paid path, clear local
  // state on the trial path).
  onRemove: () => Promise<PromoApplyResult>;
  // The promo code currently applied, or null when none is. Drives the
  // applied-state row vs the entry field.
  appliedCode: string | null;
  // The applied discount, pre-formatted for the chip (e.g. "25% off forever",
  // "$5.00 off", "20% off for 6 months"). Built by both paths via
  // promoDescriptor. Absent when nothing is applied.
  discountLabel?: string;
  // Disabled while the payment is being confirmed, so a code can't be changed
  // mid-submit.
  disabled?: boolean;
  // A parent-driven error (the campaign auto-apply's surfaced failure — the
  // visitor never opened the entry themselves): opens the entry and shows the
  // message. The visitor's own apply/clear takes over from there.
  externalError?: string | null;
}

export function PromoCodeInput({
  onApply,
  onRemove,
  appliedCode,
  discountLabel,
  disabled,
  externalError,
}: Props) {
  useT();
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // A campaign auto-apply failure surfaces after mount: open the entry with
  // the reason so the promised-discount miss isn't silent.
  useEffect(() => {
    if (externalError) {
      setExpanded(true);
      setError(externalError);
    }
  }, [externalError]);

  // Focus the field when it's revealed. The `autofocus` attribute (used elsewhere
  // for step-transition mounts) doesn't fire here: this is a conditional swap
  // inside an already-mounted step, and browsers gate dynamic autofocus behind a
  // fresh mount/activation, so it leaves focus where it was.
  useEffect(() => {
    if (expanded) {
      inputRef.current?.focus();
    }
  }, [expanded]);

  const apply = async () => {
    const trimmed = code.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await onApply(trimmed);
      if (!result.ok) {
        setError(result.message || t('promo.invalid', { code: trimmed }));
        return;
      }
      // The parent now reflects the applied code, which flips this to the
      // applied-state row; clear the entry so a later remove starts fresh.
      setCode('');
    } catch (err) {
      logError('apply promo code failed', err);
      setError(t('promo.applyFailedRetry'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await onRemove();
      if (!result.ok) {
        setError(result.message || t('promo.removeFailed'));
        return;
      }
    } catch (err) {
      logError('remove promo code failed', err);
      setError(t('promo.removeFailedRetry'));
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    // Intercept Enter so it applies the code instead of submitting the payment
    // form (which would trigger payment) when the entry renders inside one —
    // the checkout steps portal it out to the sidebar, but standalone
    // embeddings render it inline in the form.
    if (e.key === 'Enter') {
      e.preventDefault();
      void apply();
    }
  };

  if (appliedCode) {
    return (
      <div class="sm-promo notranslate">
        <div class="sm-promo-applied">
          <AppliedPromoChip code={appliedCode} label={discountLabel} />
          <button
            type="button"
            class="sm-promo-remove"
            onClick={remove}
            disabled={disabled || busy}
          >
            {/* No busy text: a content-width button/link grows with the
                translated string (de "Wird angewendet…" vs "Anwenden") and
                shifts the row mid-action — the disabled state is the signal. */}
            {t('promo.remove')}
          </button>
        </div>
        {error && <p class="sm-promo-error" role="alert">{error}</p>}
      </div>
    );
  }

  if (!expanded) {
    return (
      <div class="sm-promo">
        <button
          type="button"
          class="sm-promo-toggle"
          onClick={() => setExpanded(true)}
          disabled={disabled}
        >
          {t('promo.toggle')}
        </button>
      </div>
    );
  }

  return (
    <div class="sm-promo">
      <label class="sm-promo-label" for="sm-promo-code">{t('promo.label')}</label>
      <div class="sm-promo-row">
        <input
          ref={inputRef}
          id="sm-promo-code"
          class="sm-input sm-promo-field notranslate"
          type="text"
          autocomplete="off"
          autocapitalize="characters"
          spellcheck={false}
          placeholder={t('promo.placeholder')}
          value={code}
          disabled={disabled || busy}
          onInput={(e) => setCode((e.target as HTMLInputElement).value)}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          class="sm-btn sm-promo-apply"
          onClick={apply}
          disabled={disabled || busy || !code.trim()}
        >
          {t('promo.apply')}
        </button>
      </div>
      {error && <p class="sm-promo-error" role="alert">{error}</p>}
    </div>
  );
}
